/**
 * DataTableShortcuts Component
 * 
 * Provides keyboard shortcut support for DataTable tabs, filters, and pagination.
 * Shortcuts are scoped to the DataTable container only.
 * 
 * Usage:
 * - Option/Alt + 1-9: Activate tab or open filter by index
 * - Tabs are numbered first, then filters from left to right
 * - Arrow Left/Right: Navigate pages (when pagination is registered)
 * - Option/Alt + [ / ]: Cycle per-page options (when pagination is registered)
 */

import { createContext, useContext, useCallback, useRef, useEffect, type ReactNode } from 'react';
import { useThrottleCheck } from '@hooks/useThrottle';
import { SHORTCUT_THROTTLE_MS } from './constants';

type ShortcutHandler = () => void;

interface ShortcutRegistration {
  index: number;
  handler: ShortcutHandler;
  type: 'tab' | 'filter';
}

interface PaginationShortcuts {
  canGoPrevious: boolean;
  canGoNext: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onCyclePerPage?: (direction: 'prev' | 'next') => void;
}

interface DataTableShortcutsContextValue {
  register: (index: number, handler: ShortcutHandler, type: 'tab' | 'filter') => () => void;
  registerPagination: (shortcuts: PaginationShortcuts) => () => void;
}

const DataTableShortcutsContext = createContext<DataTableShortcutsContextValue | null>(null);

export function useDataTableShortcuts() {
  return useContext(DataTableShortcutsContext);
}

interface DataTableShortcutsProps {
  children: ReactNode;
  /** Enable/disable shortcuts (e.g., disable when a modal is open) */
  enabled?: boolean;
}

export function DataTableShortcuts({ children, enabled = true }: DataTableShortcutsProps) {
  const registrationsRef = useRef<Map<string, ShortcutRegistration>>(new Map());
  const paginationRef = useRef<PaginationShortcuts | null>(null);
  const canTriggerShortcut = useThrottleCheck(SHORTCUT_THROTTLE_MS);

  const register = useCallback((index: number, handler: ShortcutHandler, type: 'tab' | 'filter') => {
    const key = `${type}-${index}`;
    registrationsRef.current.set(key, { index, handler, type });
    
    return () => {
      registrationsRef.current.delete(key);
    };
  }, []);

  const registerPagination = useCallback((shortcuts: PaginationShortcuts) => {
    paginationRef.current = shortcuts;
    return () => {
      paginationRef.current = null;
    };
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;

    const isInDropdown = target.closest('[role="menu"]') !== null;
    const isInPopover = target.closest('[data-radix-popper-content-wrapper]') !== null;
    const hasOpenDropdown = document.querySelector('[data-state="open"][role="menu"]') !== null;

    if (isInDropdown || isInPopover) return;

    const pagination = paginationRef.current;

    // Option+[ / Option+] to cycle per-page options
    if (e.altKey && (e.code === 'BracketLeft' || e.code === 'BracketRight') && pagination?.onCyclePerPage) {
      if (!canTriggerShortcut()) return;
      e.preventDefault();
      pagination.onCyclePerPage(e.code === 'BracketLeft' ? 'prev' : 'next');
      return;
    }

    // Arrow left/right for pagination - but not when focused on tabs or other controls
    const isInTablist = target.closest('[role="tablist"]') !== null;
    const isOnButton = target.tagName === 'BUTTON' && !target.closest('table');

    if (pagination && !hasOpenDropdown && !isInTablist && !isOnButton) {
      if (e.key === 'ArrowLeft' && pagination.canGoPrevious) {
        if (!canTriggerShortcut()) return;
        e.preventDefault();
        pagination.onPreviousPage();
        return;
      }
      if (e.key === 'ArrowRight' && pagination.canGoNext) {
        if (!canTriggerShortcut()) return;
        e.preventDefault();
        pagination.onNextPage();
        return;
      }
    }

    // Option/Alt + number for tabs/filters
    if (!e.altKey) return;

    const match = e.code.match(/^Digit(\d)$/);
    if (!match) return;
    const num = parseInt(match[1], 10);
    if (num < 1 || num > 9) return;

    // Build ordered list: tabs first (by index), then filters (by index)
    const registrations = Array.from(registrationsRef.current.values());
    const tabs = registrations.filter(r => r.type === 'tab').sort((a, b) => a.index - b.index);
    const filters = registrations.filter(r => r.type === 'filter').sort((a, b) => a.index - b.index);
    const ordered = [...tabs, ...filters];

    const shortcutTarget = ordered[num - 1];
    if (shortcutTarget) {
      e.preventDefault();
      e.stopPropagation();
      shortcutTarget.handler();
    }
  }, [enabled, canTriggerShortcut]);

  // Listen at document level so shortcuts work without needing focus
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <DataTableShortcutsContext.Provider value={{ register, registerPagination }}>
      {children}
    </DataTableShortcutsContext.Provider>
  );
}
